import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExternalLink, Phone, Download, AlertTriangle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { PageHero } from "@/components/common";
import useTitle from "@/lib/useTitle";
import { fileUrl } from "@/lib/api";

const TABS = [["article", "Artigos"], ["faq", "Perguntas frequentes"], ["myth", "Mitos e factos"], ["support", "Recursos de apoio"], ["download", "Materiais"]];

const Source = ({ r }) => r.source ? <p className="mt-3 text-xs text-slate-500">Fonte: {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">{r.source}<ExternalLink className="h-3 w-3" aria-hidden /></a> : r.source}</p> : null;

export default function SaudeMental() {
  useTitle("Saúde Mental para Todos");
  const { data } = useSite();
  const { hash } = useLocation();
  const target = data.resources.find((r) => `#${r.slug}` === hash);
  const [tab, setTab] = useState(target?.type || "article");
  useEffect(() => { if (target) { setTab(target.type); setTimeout(() => document.getElementById(target.slug)?.scrollIntoView({ block: "center" }), 100); } }, [target]);
  const items = data.resources.filter((r) => r.type === tab);

  return (
    <>
      <PageHero eyebrow="Literacia em saúde mental" title="Saúde Mental para Todos" tone="#0f766e" image="/img/sunlight.jpg">Informação clara, de fontes credíveis, para compreender, cuidar e procurar apoio.</PageHero>
      <div className="container-x py-10">
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex gap-3" role="note" data-testid="mh-disclaimer">
          <AlertTriangle className="h-6 w-6 text-amber-700 shrink-0" aria-hidden />
          <p className="text-sm text-amber-950">Estes conteúdos são informativos e não substituem o aconselhamento de profissionais de saúde. Não fazemos diagnósticos nem consultas. Em caso de emergência, ligue <a href="tel:112" className="font-bold underline">112</a>. Para aconselhamento psicológico, ligue SNS 24: <a href="tel:808242424" className="font-bold underline">808 24 24 24</a>.</p>
        </div>
        <div className="mt-8 flex gap-2 overflow-x-auto no-scrollbar" role="tablist" aria-label="Tipo de conteúdo">
          {TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`mh-tab-${k}`} className={`shrink-0 h-12 px-5 rounded-full font-semibold border ${tab === k ? "bg-[var(--ink)] text-white" : "bg-white"}`}>{l}</button>)}
        </div>
        <div className="mt-8" role="tabpanel" data-testid={`mh-panel-${tab}`}>
          {items.length === 0 && <p className="text-slate-600">Conteúdos em preparação.</p>}
          {tab === "faq" ? (
            <Accordion type="single" collapsible defaultValue={target?.slug} className="bg-white rounded-3xl border px-6">
              {items.map((r) => <AccordionItem key={r.id} value={r.slug} id={r.slug}><AccordionTrigger className="font-display text-lg text-left min-h-[56px]" data-testid={`faq-${r.slug}`}>{r.title}</AccordionTrigger><AccordionContent className="text-base text-slate-700">{r.body}<Source r={r} /></AccordionContent></AccordionItem>)}
            </Accordion>
          ) : tab === "myth" ? (
            <ul className="grid gap-4 md:grid-cols-3">
              {items.map((r) => (
                <li key={r.id} id={r.slug} className="rounded-3xl overflow-hidden border bg-white" data-testid={`myth-${r.slug}`}>
                  <div className="p-6 bg-rose-50"><p className="eyebrow text-rose-800">Mito</p><p className="font-display text-xl font-bold mt-2 line-through decoration-rose-400/70">{r.title}</p></div>
                  <div className="p-6"><p className="eyebrow text-[var(--leaf)]">Facto</p><p className="mt-2 text-slate-800">{r.body}</p><Source r={r} /></div>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {items.map((r) => (
                <li key={r.id} id={r.slug} className="rounded-3xl border bg-white p-6 flex flex-col" data-testid={`resource-${r.slug}`}>
                  {tab === "support" && <Phone className="h-6 w-6 text-[var(--leaf)] mb-3" aria-hidden />}
                  <h2 className="font-display text-xl font-bold">{r.title}</h2>
                  <p className="mt-3 text-slate-700">{r.body}</p>
                  <Source r={r} />
                  {r.file && <a href={fileUrl(r.file)} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 h-11 px-4 rounded-full bg-[var(--leaf)] text-white font-semibold self-start" data-testid={`resource-download-${r.slug}`}><Download className="h-4 w-4" aria-hidden />Descarregar</a>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="mt-10 text-xs text-slate-500">Os conteúdos são publicados após validação editorial da AASPS.</p>
      </div>
    </>
  );
}
